import { useInstitutionsStore } from '@/stores/institutions';
import { useOrganizationsStore } from '@/stores/organizations';
import { ESymptomTriage } from '@/types';

// 2FA is left out on purpose: it is decided across all of the user's
// institutions, not just the current one (see the institutions store's
// `is2FAEnabledForAnyInstitution`)
type TFeatureFlag =
  | 'symptomTriage'
  | 'medicalStationCalls'
  | 'canarySpeech'
  | 'medwand';

type TFeatureFlags = Record<TFeatureFlag, boolean>;

/**
 * Resolves which organization-gated features are in force for the current
 * institution. A feature runs only when the institution has turned it on and
 * its organization has not closed the gate over it: an unset gate is open,
 * only an explicit `false` closes it. While the organization is not known
 * yet, every feature is treated as off so it does not flash in and out.
 * Institutions of non-admin users come without `organizationId`; those users
 * belong to a single organization, so the current one is used.
 * @returns A flag per gated feature, true when it is in force.
 */
export const useEnabledFeatures = (): TFeatureFlags => {
  const currentInstitution = useInstitutionsStore(
    (state) => state.currentInstitution,
  );
  const organization = useOrganizationsStore((state) =>
    currentInstitution?.organizationId
      ? (state.organizations.find(
          ({ id }) => id === currentInstitution.organizationId,
        ) ?? null)
      : state.currentOrganization,
  );

  const configuration = currentInstitution?.configuration;
  const isGateOpen = (feature: TFeatureFlag) =>
    !!organization && organization.configuration?.[feature] !== false;

  return {
    symptomTriage:
      isGateOpen('symptomTriage') &&
      configuration?.symptomTriage === ESymptomTriage.INTERACTIVE,
    medicalStationCalls:
      isGateOpen('medicalStationCalls') && !!configuration?.medicalStationCalls,
    canarySpeech: isGateOpen('canarySpeech') && !!configuration?.canarySpeech,
    medwand: isGateOpen('medwand') && !!configuration?.medwand,
  };
};
